import React, { useEffect, useRef, useState } from 'react';
import style from './caseStudyCards.module.scss';

export function CaseStudyTagBar({ tags, tagRows, className = '' }) {
  if (tagRows?.length) {
    return (
      <div className={`${style.tagBarRows} ${className}`.trim()}>
        {tagRows.map((row) => (
          <div key={row.join('-')} className={style.tagBarRow}>
            {row.map((t) => (
              <span key={t} className={style.tag}>
                {t}
              </span>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`${style.tagBar} ${className}`}>
      {tags.map((t) => (
        <span key={t} className={style.tag}>
          {t}
        </span>
      ))}
    </div>
  );
}

export function CaseStudyHero({
  title,
  tags,
  tagRows,
  subhead,
  ctaHref,
  ctaLabel,
  heroImageSrc,
  heroImageAlt,
  /** When set, overrides default frame ratio so `object-fit: cover` matches the asset (e.g. "1820 / 831"). */
  heroImageAspectRatio,
  className = '',
  heroRowClassName = '',
  heroLeftClassName = '',
  tagBarClassName = '',
  heroImageWrapClassName = '',
}) {
  const imageFrameStyle = heroImageAspectRatio ? { aspectRatio: heroImageAspectRatio } : undefined;
  return (
    <header className={`${style.hero} ${className}`.trim()}>
      <h1 className={style.heroTitle}>{title}</h1>
      <div className={`${style.heroRow} ${heroRowClassName}`.trim()}>
        <div className={`${style.heroLeft} ${heroLeftClassName}`.trim()}>
          <CaseStudyTagBar tags={tags} tagRows={tagRows} className={tagBarClassName} />
          <p className={style.heroSub}>{subhead}</p>
          <a className={style.ctaButton} href={ctaHref} target="_blank" rel="noreferrer">
            {ctaLabel}
          </a>
        </div>
        <div className={`${style.heroImageWrap} ${heroImageWrapClassName}`.trim()}>
          <div className={style.heroImageInner} style={imageFrameStyle}>
            <img className={style.heroImage} src={heroImageSrc} alt={heroImageAlt} />
          </div>
        </div>
      </div>
    </header>
  );
}

export function CaseStudyOverviewColumn({ label, children, wide = false }) {
  return (
    <div className={`${style.overviewCol} ${wide ? style.overviewColWide : ''}`}>
      <p className={style.overviewLabel}>{label}</p>
      {children}
    </div>
  );
}

export function CaseStudyOverviewGrid({ children }) {
  return <div className={style.overviewGrid}>{children}</div>;
}

export function CaseStudySecondaryNav({ items, activeId, onSelect }) {
  return (
    <nav className={style.secondaryNav} aria-label="Case study sections">
      {items.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          className={`${style.navLink} ${activeId === id ? style.navLinkActive : ''}`}
          onClick={() => onSelect(id)}
        >
          {label}
        </button>
      ))}
    </nav>
  );
}

export function CaseStudySectionHeader({
  kicker,
  title,
  titleAs: TitleTag = 'h2',
  titleClassName = '',
  asideImg,
  asideAlt = '',
  asideImgClassName = '',
}) {
  const head = (
    <div className={style.sectionHeader}>
      <p className={style.sectionKicker}>{kicker}</p>
      <TitleTag className={`${style.sectionTitle} ${titleClassName}`}>{title}</TitleTag>
    </div>
  );

  if (asideImg) {
    return (
      <div className={style.sectionHeaderRow}>
        {head}
        <img
          className={[style.sectionAsideImg, asideImgClassName].filter(Boolean).join(' ')}
          src={asideImg}
          alt={asideAlt}
          loading="lazy"
        />
      </div>
    );
  }

  return head;
}

export function CaseStudySplitSection({
  kicker,
  title,
  imageSrc,
  imageAlt = '',
  captionHref,
  captionKicker,
  captionLabel,
  children,
  splitFooter = null,
  largeFooterGap = false,
  footerGap80 = false,
  figureClassName = '',
  splitMedia = null,
  captionCenter = false,
}) {
  const figureCls = [style.splitFigure, figureClassName].filter(Boolean).join(' ');
  const captionWrapCls = [style.mediaCaption, captionCenter && style.mediaCaptionCenter].filter(Boolean).join(' ');
  const captionPlainCls = [style.mediaCaptionPlain, captionCenter && style.mediaCaptionPlainCenter]
    .filter(Boolean)
    .join(' ');
  const captionKickerCls = [style.mediaCaptionKicker, captionCenter && style.mediaCaptionKickerCenter]
    .filter(Boolean)
    .join(' ');

  const defaultMedia = (
    <>
      <figure className={figureCls}>
        <img className={style.splitImg} src={imageSrc} alt={imageAlt} loading="lazy" />
      </figure>
      {captionHref ? (
        <div className={captionWrapCls}>
          {captionKicker ? <p className={captionKickerCls}>{captionKicker}</p> : null}
          <a className={style.mediaLink} href={captionHref} target="_blank" rel="noreferrer">
            {captionLabel}
          </a>
        </div>
      ) : captionLabel || captionKicker ? (
        <div className={captionWrapCls}>
          {captionKicker ? <p className={captionKickerCls}>{captionKicker}</p> : null}
          {captionLabel ? <p className={captionPlainCls}>{captionLabel}</p> : null}
        </div>
      ) : null}
    </>
  );

  return (
    <section className={style.sectionBlock}>
      <CaseStudySectionHeader kicker={kicker} title={title} />
      <div
        className={`${style.splitContent} ${largeFooterGap ? style.splitContentGap120 : ''} ${footerGap80 ? style.splitContentGap80 : ''}`.trim()}
      >
        <div className={style.splitRow}>
          <div className={style.splitText}>{children}</div>
          <div className={style.splitMedia}>{splitMedia ?? defaultMedia}</div>
        </div>
        {splitFooter ? <div className={style.splitFooter}>{splitFooter}</div> : null}
      </div>
    </section>
  );
}

function CardLabel({ tone, children }) {
  const cls = tone === 'teal' ? style.cardLabelTeal : style.cardLabelMuted;
  return <p className={cls}>{children}</p>;
}

export function CaseStudyBulletList({ items, className = '' }) {
  return (
    <ul className={`${style.cardList} ${className}`.trim()}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export function CaseStudyContentCard({
  label,
  labelTone = 'muted',
  surface = 'porcelain',
  children,
  className = '',
}) {
  const surf = surface === 'tan' ? style.surfaceTan : surface === 'none' ? '' : style.surfacePorcelain;
  return (
    <div className={`${style.contentCard} ${surf} ${className}`.trim()}>
      {label ? <CardLabel tone={labelTone}>{label}</CardLabel> : null}
      {children}
    </div>
  );
}

export function CaseStudyDoubleCardRow({ left, right, className = '' }) {
  return (
    <div className={`${style.doubleRow} ${className}`.trim()}>
      {left}
      {right}
    </div>
  );
}

export function CaseStudyFindingHeader({ number, title, mark = null, showMark = true }) {
  return (
    <div className={style.findingHeader}>
      {showMark ? (
        <div
          className={`${style.findingMark} ${mark ? style.findingMarkGraphic : ''}`.trim()}
          aria-hidden
        >
          {mark ?? (
            <>
              <span className={style.findingHash}>#</span>
              <span className={style.findingNum}>{number}</span>
            </>
          )}
        </div>
      ) : null}
      <h2 className={style.findingTitle}>{title}</h2>
    </div>
  );
}

export function CaseStudyParticipantQuote({ avatarSrc, avatarAlt, quote }) {
  return (
    <figure className={style.quoteRow}>
      <img className={style.quoteAvatar} src={avatarSrc} alt={avatarAlt} loading="lazy" />
      <blockquote className={style.quoteText}>
        <p>{quote}</p>
      </blockquote>
    </figure>
  );
}

export function CaseStudyProse({ children, as: Tag = 'p', className = '' }) {
  return <Tag className={`${style.proseWide} ${className}`}>{children}</Tag>;
}

export function CaseStudyDivider({ imageSrc, alt = '' }) {
  return (
    <div className={style.divider} aria-hidden={!alt}>
      <img className={style.dividerImg} src={imageSrc} alt={alt} />
    </div>
  );
}

function DonutStat({
  value,
  label,
  progress,
  captionSize = 'md',
  animateOnScroll = false,
  animationIndex = 0,
  staggerModulo,
}) {
  // Ring near viewBox edge + thin stroke → large inner hole (see Figma Stats Bar).
  const r = 55;
  const c = 2 * Math.PI * r;
  const targetOffset = c * (1 - Math.min(1, Math.max(0, progress)));
  const wrapRef = useRef(null);
  const [fillStarted, setFillStarted] = useState(() => {
    if (!animateOnScroll) return true;
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (!animateOnScroll) return undefined;

    if (typeof window === 'undefined') return undefined;

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) {
      setFillStarted(true);
      return undefined;
    }

    const el = wrapRef.current;
    if (!el) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setFillStarted(true));
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [animateOnScroll]);

  const arcOffset = fillStarted ? targetOffset : c;
  const fillDurationSec = 2.1;
  const staggerSlot =
    staggerModulo != null && staggerModulo > 0 ? animationIndex % staggerModulo : animationIndex;
  const fillDelayMs = 700 + staggerSlot * 70;
  const arcTransition =
    animateOnScroll && !fillStarted
      ? undefined
      : animateOnScroll
        ? `stroke-dashoffset ${fillDurationSec}s cubic-bezier(0.22, 1, 0.36, 1) ${fillDelayMs}ms`
        : undefined;

  return (
    <div className={style.donutWrap} ref={animateOnScroll ? wrapRef : undefined}>
      <svg className={style.donutSvg} viewBox="0 0 120 120" aria-hidden>
        <circle className={style.donutTrack} cx="60" cy="60" r={r} />
        <circle
          className={style.donutArc}
          cx="60"
          cy="60"
          r={r}
          strokeDasharray={c}
          strokeDashoffset={arcOffset}
          style={{ transition: arcTransition }}
        />
      </svg>
      <div className={style.donutCenter}>
        <span className={style.donutValue}>{value}</span>
        <span
          className={`${style.donutCaption} ${captionSize === 'sm' ? style.donutCaptionSm : ''}`.trim()}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

export function CaseStudyStatsBar({
  stats,
  className = '',
  donutVariant = 'default',
  animateOnScroll = false,
  staggerModulo,
}) {
  const donutMod = donutVariant === 'cdOutcome' ? style.statsBarDonutCdOutcome : '';
  return (
    <div className={`${style.statsBar} ${donutMod} ${className}`.trim()}>
      {stats.map((s, i) => (
        <div key={`${s.value}-${s.label}-${i}`}>
          <DonutStat
            value={s.value}
            label={s.label}
            progress={s.progress}
            captionSize={s.captionSize}
            animateOnScroll={animateOnScroll}
            animationIndex={i}
            staggerModulo={staggerModulo}
          />
        </div>
      ))}
    </div>
  );
}

export function CaseStudyFindingBlock({ children }) {
  return <div className={style.findingSection}>{children}</div>;
}
